/**
 * DRPX Courier — Team (DRPX-Pro, Lieferung 2, Migration 141)
 *
 * Chef: Team lesen, Rechte setzen, sperren/entsperren (Datenbank-Funktionen),
 * einladen/erneut senden/entfernen (Edge Functions `team-einladen`,
 * `team-entfernen` — dort wird der Zugang in Supabase Auth angelegt bzw.
 * gelöscht, das geht nur mit dem Dienst-Schlüssel).
 * Mitarbeiter: eigene Rechte lesen, Einladung annehmen.
 *
 * Fehler kommen als feste Codes zurück (z. B. `team_grenze`,
 * `email_vergeben`), die Oberfläche übersetzt sie über `teamFehlerText`.
 *
 * @module lib/supabase/team
 */

import { _sb } from './client.js';
import type { TeamMitglied, TeamRechte } from '../../types.js';
import { URHEBER_SPALTE, inStuecken, type TeamName, type UrheberArt } from '../urheber.js';

const KEIN_CLIENT = 'kein_client';

/** Ergebnis der Team-Aufrufe: `fehler` = fester Code oder Text. */
export interface TeamErgebnis<T = null> {
  data: T | null;
  fehler: string | null;
}

/** Bekannte Codes aus Datenbank-Meldungen herausziehen (Rest: Rohtext). */
export function teamFehlerCode(msg: string | undefined | null): string {
  const m = msg || '';
  const bekannt = ['team_grenze', 'team_kein_pro', 'team_nicht_angenommen', 'team_gesperrt'];
  for (const code of bekannt) if (m.includes(code)) return code;
  if (m.includes('Nur der Chef')) return 'nur_chef';
  return m || 'unbekannt';
}

function rechteAus(row: Record<string, unknown>): TeamRechte {
  return {
    zuschlag: row.darf_zuschlag === true,
    bieten: row.darf_bieten === true,
    abrechnung: row.darf_abrechnung === true,
  };
}

/**
 * Eigene Rechte eines Mitarbeiters (eigene Zeile in `firma_mitglieder`,
 * per RLS lesbar). null bei Fehler oder ohne Zeile.
 */
export async function getMeineTeamRechte(personId: string): Promise<TeamRechte | null> {
  if (!_sb || !personId) return null;
  try {
    const r = await _sb
      .from('firma_mitglieder')
      .select('darf_zuschlag, darf_bieten, darf_abrechnung')
      .eq('user_id', personId)
      .maybeSingle();
    if (r.error || !r.data) return null;
    return rechteAus(r.data as Record<string, unknown>);
  } catch {
    return null;
  }
}

/** Team des angemeldeten Chefs (leer für alle anderen). */
export async function getMeinTeam(): Promise<TeamErgebnis<TeamMitglied[]>> {
  if (!_sb) return { data: null, fehler: KEIN_CLIENT };
  try {
    const r = await _sb.rpc('get_mein_team');
    if (r.error) return { data: null, fehler: teamFehlerCode(r.error.message) };
    const rows = (r.data || []) as Record<string, unknown>[];
    return {
      data: rows.map(function (row): TeamMitglied {
        return {
          userId: String(row.user_id),
          email: String(row.email || ''),
          vorname: String(row.vorname || ''),
          nachname: String(row.nachname || ''),
          status: row.status as TeamMitglied['status'],
          rechte: rechteAus(row),
          eingeladenAm: String(row.eingeladen_am || ''),
          angenommenAm: row.angenommen_am ? String(row.angenommen_am) : null,
        };
      }),
      fehler: null,
    };
  } catch (e: unknown) {
    return { data: null, fehler: e instanceof Error ? e.message : String(e) };
  }
}

/** Rechte eines Mitarbeiters setzen (nur Chef). */
export async function teamRechteSetzen(userId: string, rechte: TeamRechte): Promise<TeamErgebnis> {
  if (!_sb) return { data: null, fehler: KEIN_CLIENT };
  try {
    const r = await _sb.rpc('team_rechte_setzen', {
      p_user: userId,
      p_zuschlag: rechte.zuschlag,
      p_bieten: rechte.bieten,
      p_abrechnung: rechte.abrechnung,
    });
    return { data: null, fehler: r.error ? teamFehlerCode(r.error.message) : null };
  } catch (e: unknown) {
    return { data: null, fehler: e instanceof Error ? e.message : String(e) };
  }
}

/** Mitarbeiter sperren oder wieder freigeben (nur Chef). */
export async function teamStatusSetzen(userId: string, status: 'aktiv' | 'gesperrt'): Promise<TeamErgebnis> {
  if (!_sb) return { data: null, fehler: KEIN_CLIENT };
  try {
    const r = await _sb.rpc('team_status_setzen', { p_user: userId, p_status: status });
    return { data: null, fehler: r.error ? teamFehlerCode(r.error.message) : null };
  } catch (e: unknown) {
    return { data: null, fehler: e instanceof Error ? e.message : String(e) };
  }
}

/** Edge Function aufrufen; Fehlercode aus der Antwort lesen. */
async function rufeFunktion(name: string, body: Record<string, unknown>): Promise<TeamErgebnis<Record<string, unknown>>> {
  if (!_sb) return { data: null, fehler: KEIN_CLIENT };
  try {
    const res = await _sb.functions.invoke(name, { body });
    if (res.error) {
      // Bei Status ≠ 2xx steckt unsere Antwort ({ error: 'code' }) in context.
      const ctx = (res.error as { context?: { json?: () => Promise<unknown> } }).context;
      if (ctx && typeof ctx.json === 'function') {
        try {
          const b = (await ctx.json()) as { error?: string } | null;
          if (b && typeof b.error === 'string') return { data: null, fehler: b.error };
        } catch { /* kein JSON */ }
      }
      return { data: null, fehler: res.error.message || 'unbekannt' };
    }
    const raw: unknown = res.data;
    const data = (typeof raw === 'string' ? JSON.parse(raw) : raw) as Record<string, unknown> | null;
    if (data && typeof data.error === 'string') return { data: null, fehler: data.error };
    return { data: data || {}, fehler: null };
  } catch (e: unknown) {
    return { data: null, fehler: e instanceof Error ? e.message : String(e) };
  }
}

/** Eingaben für eine neue Einladung. */
export interface EinladungEingabe {
  email: string;
  vorname: string;
  nachname: string;
  rechte: TeamRechte;
  /** Sprache der Mail (Supabase-Vorlage „Invite user"). */
  lang: string;
}

/** Mitarbeiter per E-Mail einladen (Edge Function `team-einladen`). */
export async function teamEinladen(e: EinladungEingabe): Promise<TeamErgebnis<Record<string, unknown>>> {
  return rufeFunktion('team-einladen', {
    email: e.email.trim().toLowerCase(),
    vorname: e.vorname.trim(),
    nachname: e.nachname.trim(),
    darfZuschlag: e.rechte.zuschlag,
    darfBieten: e.rechte.bieten,
    darfAbrechnung: e.rechte.abrechnung,
    lang: e.lang,
  });
}

/** Offene Einladung erneut senden (alter Link wird ungültig). */
export async function teamEinladungErneut(userId: string, lang: string): Promise<TeamErgebnis<Record<string, unknown>>> {
  return rufeFunktion('team-einladen', { erneut: userId, lang });
}

/** Mitarbeiter (oder offene Einladung) endgültig entfernen. */
export async function teamEntfernen(userId: string): Promise<TeamErgebnis<Record<string, unknown>>> {
  return rufeFunktion('team-entfernen', { userId });
}

/**
 * Einladung annehmen (Mitarbeiter, nach dem Festlegen des Passworts):
 * Status „aktiv" und Zustimmung zur Datenschutzerklärung (Version +
 * Browser, wie bei der Registrierung).
 */
export async function einladungAnnehmen(dsgvoVersion: string, ua: string): Promise<TeamErgebnis> {
  if (!_sb) return { data: null, fehler: KEIN_CLIENT };
  try {
    const r = await _sb.rpc('einladung_annehmen', {
      p_dsgvo_version: dsgvoVersion,
      p_ua: ua.slice(0, 1000),
    });
    return { data: null, fehler: r.error ? teamFehlerCode(r.error.message) : null };
  } catch (e: unknown) {
    return { data: null, fehler: e instanceof Error ? e.message : String(e) };
  }
}

/**
 * Namen aller Zugänge der eigenen Firma (Chef + aktive/gesperrte
 * Mitarbeiter) für „Angelegt von" — RPC `get_team_namen` (Migration 142).
 * Fremde Profile sind per RLS gesperrt, darum über die Datenbank-Funktion.
 *
 * @returns Namen nach Kennung; null bei Fehler (z. B. Migration 142 fehlt)
 */
export async function getTeamNamen(): Promise<Record<string, TeamName> | null> {
  if (!_sb) return null;
  try {
    const r = await _sb.rpc('get_team_namen');
    if (r.error) return null;
    const out: Record<string, TeamName> = {};
    ((r.data || []) as Record<string, unknown>[]).forEach(function (row) {
      out[String(row.person_id)] = {
        vorname: String(row.vorname || ''),
        nachname: String(row.nachname || ''),
        istChef: row.ist_chef === true,
      };
    });
    return out;
  } catch {
    return null;
  }
}

/**
 * Wer hat es angelegt? Liest `urheber` (Migration 138; per RLS nur Zeilen
 * der eigenen Firma). Abfrage in Stücken zu 100 Kennungen, damit die Adresse
 * nicht zu lang wird.
 *
 * @param art Auftrag, Gebot, Foto oder Dokument
 * @param ids Kennungen der Aufträge/Gebote/Fotos/Dokumente
 * @returns Kennung → Person (null = Zugang entfernt). Ohne Eintrag (fremde
 *          Firma, Altbestand) fehlt der Schlüssel. null bei Fehler.
 */
export async function getUrheber(
  art: UrheberArt,
  ids: string[],
): Promise<Record<string, string | null> | null> {
  if (!_sb) return null;
  const sb = _sb;
  const spalte = URHEBER_SPALTE[art];
  const eindeutig = Array.from(new Set(ids.filter(Boolean)));
  if (eindeutig.length === 0) return {};
  try {
    const antworten = await Promise.all(
      inStuecken(eindeutig, 100).map(function (stueck) {
        return sb.from('urheber').select(spalte + ', person_id').in(spalte, stueck);
      }),
    );
    const out: Record<string, string | null> = {};
    for (const r of antworten) {
      if (r.error) return null;
      ((r.data || []) as unknown as Record<string, unknown>[]).forEach(function (row) {
        const id = row[spalte];
        if (typeof id === 'string') out[id] = typeof row.person_id === 'string' ? row.person_id : null;
      });
    }
    return out;
  } catch {
    return null;
  }
}

/**
 * DRPX-Pro in der Probezeit kostenlos testen (RPC `pro_testen`, Migration
 * 144). Nur der Chef einer Firma ohne bezahltes Abo; setzt `abo_tier = 'pro'`.
 *
 * @returns `data` = Ende der Probezeit (ISO), `fehler` = `pro_keine_probezeit`,
 *          `pro_nur_chef` oder Rohtext
 */
export async function proTesten(): Promise<TeamErgebnis<string>> {
  if (!_sb) return { data: null, fehler: KEIN_CLIENT };
  try {
    const r = await _sb.rpc('pro_testen');
    if (r.error) {
      const m = r.error.message || '';
      const code = ['pro_keine_probezeit', 'pro_nur_chef'].find((c) => m.includes(c));
      return { data: null, fehler: code || m || 'unbekannt' };
    }
    return { data: typeof r.data === 'string' ? r.data : null, fehler: null };
  } catch (e: unknown) {
    return { data: null, fehler: e instanceof Error ? e.message : String(e) };
  }
}
